import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalStorage } from "../../hooks";
import { userState } from "../../redux/reducers/userReducer";

import noEventImage from "../../assets/noEventImage.svg";
import { TwButton, TwConfirmDialog, TwContainer, TwDropzone, TwFormHelperText, TwInputGroup, TwLabel, TwMainLayout, TwSelectGroup, TwSwitch } from "../../components/tailwind";
import TwDrawer from "../../components/tailwind/TwDrawer";
import CommissionModelBlock from "./CommissionModelBlock";
import { clearLoadingData, loadingState, setActionLoading, setGlobalLoading, setSectionLoading } from "../../redux/reducers/loadingReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForEvent } from "../../utils/confighelpers";
import { commissionType } from "../../utils/constants";
import { PlusIcon } from "@heroicons/react/20/solid";
import { EnvelopeIcon, CalendarDaysIcon, UserGroupIcon, ChartBarIcon, ClockIcon, MapPinIcon, PaperAirplaneIcon, PencilSquareIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Radio, RadioGroup, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { decimalDisplayValue, decimalOnChange } from "../../helpers";
import { useToast } from "../../provider/ToastProvider";

// Convert a stored (UTC) date into the "YYYY-MM-DDTHH:mm" string a
// <input type="datetime-local"> expects, expressed in the user's LOCAL time.
// Using toISOString() directly would feed UTC into a local-time field, drifting
// the value by the timezone offset on every load/save round-trip.
function toLocalDatetimeInput(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

// Shared theme for the three promotion-card actions (performance/invite/edit),
// so they read as one set of peers rather than three different weights.
const cardActionClass =
  "inline-flex items-center justify-center gap-1.5 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-800 text-sm font-semibold py-2.5 px-3 rounded-xl transition-colors min-w-0";

/**
 * Split "Pay" control: a black button that opens Pay-online (Stripe) / Mark-as-paid
 * offline. Used both as the card's primary "Pay promoter" action (full outstanding)
 * and inline on a payout-request row (the requested amount). "Pay online" disables
 * itself when the promoter has no connected Stripe account.
 */
function PayMenu({ label, hasStripe, canPayOffline = true, disabled, variant = "primary", onOnline, onOffline, t }) {
  const primary = variant !== "row";
  const btn = (primary
    ? "inline-flex items-center gap-1 rounded-xl px-3 py-1.5 text-sm font-medium shadow-sm "
    : "inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium ") +
    (disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-black");
  const ic = primary ? "size-4" : "size-3.5";
  return (
    <Menu as="div" className="relative">
      <MenuButton disabled={disabled} className={btn}>
        <BanknotesIcon className={ic} />
        {label}
        <ChevronDownIcon className={ic} />
      </MenuButton>
      <MenuItems anchor="bottom end" className="z-30 mt-1 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none py-1 text-sm">
        <MenuItem>
          <button
            disabled={!hasStripe}
            onClick={hasStripe ? onOnline : undefined}
            title={!hasStripe ? (t("myPromotions.noStripeHint") || "Promoter has no connected Stripe account.") : ""}
            className={`w-full text-left px-4 py-2 ${hasStripe ? "text-gray-700 data-[focus]:bg-gray-50" : "text-gray-300 cursor-not-allowed"}`}
          >
            {t("myPromotions.payOnlineStripe") || "Pay online (via Stripe)"}
          </button>
        </MenuItem>
        <MenuItem>
          <button
            disabled={!canPayOffline}
            onClick={canPayOffline ? onOffline : undefined}
            title={!canPayOffline ? (t("myPromotions.offlineBlockedHint") || "This promoter has online payments enabled — use Stripe to pay.") : ""}
            className={`w-full text-left px-4 py-2 ${canPayOffline ? "text-gray-700 data-[focus]:bg-gray-50" : "text-gray-300 cursor-not-allowed"}`}
          >
            {t("myPromotions.markPaidOffline") || "Mark as paid offline"}
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}

export default function MyPromotions() {
  const { t, i18n } = useTranslation();
  const { addSuccessToast, addErrorToast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loadingReduxState = useSelector(loadingState);
  const userReduxState = useSelector(userState);
  const [userToken] = useLocalStorage("userToken");

  const [promotionsList, setPromotionsList] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isEdit, setIsEdit] = useState(null);
  const [eventQuery, setEventQuery] = useState("");

  // Invite drawer state
  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
  const [inviteOfferId, setInviteOfferId] = useState(null);
  const [sellersList, setSellersList] = useState([]);
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);
  // Sellers already enrolled on the campaign being invited to (ticked + locked).
  const [enrolledSellerIds, setEnrolledSellerIds] = useState([]);
  // Scroll affordance for the seller list (show a "scroll for more" cue).
  const sellerListRef = React.useRef(null);
  const [canScrollSellers, setCanScrollSellers] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  // "invite" = self-serve email-link; "managed" = create channel now + email link
  const [addMode, setAddMode] = useState("invite");

  // ?campaign=<offerId> keeps the performance view in the URL, so a refresh /
  // deep link lands back on it instead of the list.
  const [searchParams, setSearchParams] = useSearchParams();

  // Campaign performance (organizer): totals + per-seller accordion
  const [perfOfferId, setPerfOfferId] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  // Which seller's which pill detail is open (one at a time). { id, pill }
  const [sellerDetail, setSellerDetail] = useState(null);
  const toggleSellerDetail = (id, pill) =>
    setSellerDetail((cur) => (cur && cur.id === id && cur.pill === pill ? null : { id, pill }));

  // Invite tracking for the performance view.
  const [inviteTrackingData, setInviteTrackingData] = useState([]);
  const [inviteTrackingLoading, setInviteTrackingLoading] = useState(false);

  // Customized-banner dialog: the seller being edited + the working image.
  const [bannerSeller, setBannerSeller] = useState(null); // { sellerId, name }
  const [bannerImage, setBannerImage] = useState("");

  const money = (n) => `€${Number(n || 0).toFixed(2)}`;

  const fetchInviteTracking = async (offerId) => {
    setInviteTrackingLoading(true);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForEvent()}commissionoffer/${offerId}/invites` });
      if (res?.data?.statusCode === 200) setInviteTrackingData(res.data.data?.invites || []);
      else setInviteTrackingData([]);
    } catch { setInviteTrackingData([]); }
    finally { setInviteTrackingLoading(false); }
  };

  const openPerformance = async (offerId, syncUrl = true) => {
    setPerfOfferId(offerId);
    if (syncUrl) setSearchParams({ campaign: offerId }, { replace: true });
    setPerfData(null);
    setSellerDetail(null);
    setPerfLoading(true);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForEvent()}commissionoffer/${offerId}/performance` });
      if (res?.data?.statusCode === 200) setPerfData(res.data.data);
    } catch (e) {
      /* ignore */
    } finally {
      setPerfLoading(false);
    }
    fetchInviteTracking(offerId);
  };
  const closePerformance = () => {
    setPerfOfferId(null);
    setPerfData(null);
    setInviteTrackingData([]);
    setSearchParams({}, { replace: true });
  };

  // Restore the performance view on refresh / deep link. Runs once on mount.
  // Also handle return from a Stripe payout Checkout (?payout=success|cancelled&offer=).
  React.useEffect(() => {
    const payoutReturn = searchParams.get("payout");
    const offerFromPayout = searchParams.get("offer");
    if (payoutReturn) {
      if (payoutReturn === "cancelled") {
        addErrorToast && addErrorToast(t("myPromotions.stripePayoutCancelled") || "Payout cancelled.");
      }
      const target = offerFromPayout || searchParams.get("campaign");
      if (payoutReturn === "success" && target) {
        // Don't wait on the webhook — reconcile this campaign right away so the
        // just-completed payout is recorded, then open the refreshed view.
        (async () => {
          try { await reconcilePayoutsFor(target); } catch (e) { /* fall through */ }
          addSuccessToast && addSuccessToast(t("myPromotions.stripePayoutSuccess") || "Payout completed.");
          openPerformance(target, true);
        })();
      } else if (target) {
        openPerformance(target, true);
      } else {
        setSearchParams({}, { replace: true });
      }
      return;
    }
    const cid = searchParams.get("campaign");
    if (cid) openPerformance(cid, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Is the seller list scrolled to the bottom? Drives the "scroll for more" cue.
  const updateSellerScrollHint = () => {
    const el = sellerListRef.current;
    if (!el) { setCanScrollSellers(false); return; }
    setCanScrollSellers(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  // Recompute when the list content / drawer visibility changes (after layout).
  React.useEffect(() => {
    const id = requestAnimationFrame(updateSellerScrollHint);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteDrawerOpen, sellersList, enrolledSellerIds, addMode, inviteResult]);
  const sendSellerDashboardLink = async (sellerId) => {
    try {
      await axiosInstance({ method: "post", url: `${getUrlForEvent()}commissionoffer/send-dashboard-link`, data: { sellerId } });
      addSuccessToast && addSuccessToast(t("myEvents.dashboardLinkSent") || "Dashboard link sent.");
    } catch (e) {
      /* ignore */
    }
  };

  const refreshPerf = () => { if (perfOfferId) openPerformance(perfOfferId); };

  const toggleDisableSeller = async (sellerId, disabled) => {
    dispatch(setActionLoading(true));
    try {
      await axiosInstance({ method: "post", url: `${getUrlForEvent()}commissionoffer/seller/disable`, data: { offerId: perfOfferId, sellerId, disabled } });
      await refreshPerf();
    } catch (e) { addErrorToast && addErrorToast(e?.message || "Failed"); }
    finally { dispatch(setActionLoading(false)); }
  };

  const payoutSellerCommission = async (sellerId) => {
    dispatch(setActionLoading(true));
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForEvent()}commissionoffer/seller/payout`, data: { offerId: perfOfferId, sellerId } });
      if (res?.data?.statusCode === 200) addSuccessToast && addSuccessToast(t("myPromotions.payoutSent") || "Payout sent.");
      else addErrorToast && addErrorToast(res?.data?.message || "Payout failed.");
      await refreshPerf();
    } catch (e) { addErrorToast && addErrorToast(e?.message || "Payout failed."); }
    finally { dispatch(setActionLoading(false)); }
  };

  // Verified Stripe rail: open a Checkout session and send the organizer to
  // Stripe to pay (iDEAL/card). The settlement is written by the webhook on
  // success; on return we refresh so the row reflects the new paid amount.
  const payViaStripe = async (sellerId, amount, initiatedBy = "organizer") => {
    dispatch(setActionLoading(true));
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForEvent()}commissionoffer/seller/payout-checkout`,
        data: { offerId: perfOfferId, sellerId, initiatedBy, ...(amount > 0 ? { amount } : {}) },
      });
      const url = res?.data?.statusCode === 200 && res.data.data && res.data.data.url;
      if (url) { window.location.href = url; return; }
      addErrorToast && addErrorToast(res?.data?.message || "Could not start Stripe payout.");
    } catch (e) {
      addErrorToast && addErrorToast(e?.message || "Could not start Stripe payout.");
    } finally {
      dispatch(setActionLoading(false));
    }
  };

  // Open the banner dialog seeded with the seller's current banner (if any).
  const openBannerDialog = (seller) => {
    setBannerSeller({ sellerId: seller.sellerId, name: seller.name || seller.sellerReferenceId || "" });
    setBannerImage(seller.bannerImage || "");
  };

  const saveSellerBanner = async (image) => {
    if (!bannerSeller) return;
    dispatch(setActionLoading(true));
    try {
      await axiosInstance({
        method: "post",
        url: `${getUrlForEvent()}commissionoffer/seller/banner`,
        data: { offerId: perfOfferId, sellerId: bannerSeller.sellerId, bannerImage: image || "" },
      });
      addSuccessToast && addSuccessToast(
        image ? (t("myPromotions.bannerSaved") || "Banner saved.") : (t("myPromotions.bannerRemoved") || "Banner removed.")
      );
      setBannerSeller(null);
      setBannerImage("");
      await refreshPerf();
    } catch (e) { addErrorToast && addErrorToast(e?.message || "Failed"); }
    finally { dispatch(setActionLoading(false)); }
  };

  const markCommissionPaid = async (sellerId, amount, initiatedBy = "organizer") => {
    if (!(amount > 0)) return;
    dispatch(setActionLoading(true));
    try {
      await axiosInstance({ method: "post", url: `${getUrlForEvent()}commissionoffer/settle`, data: { offerId: perfOfferId, sellerId, amount, note: "Marked paid", initiatedBy } });
      addSuccessToast && addSuccessToast(t("myPromotions.markedPaidAwaitingConfirmation") || "Payment recorded — waiting for seller to confirm receipt.");
      await refreshPerf();
    } catch (e) { addErrorToast && addErrorToast(e?.message || "Failed"); }
    finally { dispatch(setActionLoading(false)); }
  };

  const [syncingPayouts, setSyncingPayouts] = useState(false);

  // Reconcile a campaign's Stripe payouts (records any paid-but-unrecorded ones).
  // Returns the number recorded. Used both by the manual button and automatically
  // right after a successful Checkout return, so a missed webhook never matters.
  const reconcilePayoutsFor = async (offerId) => {
    const oid = offerId || perfOfferId;
    if (!oid) return 0;
    const res = await axiosInstance({ method: "post", url: `${getUrlForEvent()}commissionoffer/payout/reconcile`, data: { offerId: oid } });
    return (res?.data?.statusCode === 200 && res.data.data?.recordedCount) || 0;
  };

  // Safety net button: pull any Stripe payouts that were paid but never recorded
  // (e.g. a missed webhook) and refresh, so the ledger self-heals.
  const syncStripePayouts = async () => {
    if (!perfOfferId) return;
    setSyncingPayouts(true);
    try {
      const n = await reconcilePayoutsFor(perfOfferId);
      addSuccessToast && addSuccessToast(
        n > 0
          ? `${t("myPromotions.payoutsSynced") || "Payouts synced"} (${n})`
          : (t("myPromotions.payoutsUpToDate") || "Already up to date.")
      );
      if (n > 0) await refreshPerf();
    } catch (e) {
      addErrorToast && addErrorToast(e?.message || "Sync failed.");
    } finally {
      setSyncingPayouts(false);
    }
  };

  const declinePayoutRequest = async (requestId) => {
    if (!requestId) return;
    dispatch(setActionLoading(true));
    try {
      await axiosInstance({ method: "post", url: `${getUrlForEvent()}commissionoffer/payout-request/reject`, data: { requestId } });
      addSuccessToast && addSuccessToast(t("myPromotions.requestDeclined") || "Payout request declined.");
      await refreshPerf();
    } catch (e) { addErrorToast && addErrorToast(e?.message || "Failed"); }
    finally { dispatch(setActionLoading(false)); }
  };

  const rateLabel = (b) => (b.unitType === "percentageOnTicketPrice" ? `${b.rate}%` : `€${Number(b.rate || 0).toFixed(2)}`);
  const bandRange = (b) => (b.to != null ? `${b.from}–${b.to}` : `${b.from}+`);

  const defaultFormData = {
    eventScheduleId: "",
    label: "",
    isCommissionApplicable: true,
    commissionModelType: "flat",
    unitType: "flatFeePerTicket",
    flatValue: 0,
    tiers: [{ from: 1, commissionPerTicket: 1 }],
    isAddBonus: false,
    bonusThreshold: 0,
    bonusAmount: 0,
    maxSellers: 0,
    status: "active",
    startDate: "",
    endDate: "",
    allowedTicketTypes: [],
    applyToAddons: false,
    allowedAddonIds: [],
    addonSameAsTickets: true,
  };

  const defaultAddonForm = {
    commissionModelType: "flat",
    unitType: "flatFeePerTicket",
    flatValue: 0,
    isAddBonus: false,
    bonusThreshold: 0,
    bonusAmount: 0,
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [tiers, setTiers] = useState([{ from: 1, commissionPerTicket: 1 }]);
  const [addonForm, setAddonForm] = useState(defaultAddonForm);
  const [addonTiers, setAddonTiers] = useState([{ from: 1, commissionPerTicket: 1 }]);

  // The schedule selected in the drawer (for its ticket types + add-ons).
  const selectedSchedule = React.useMemo(
    () => eventsList.find((ev) => ev._id === formData.eventScheduleId) || null,
    [eventsList, formData.eventScheduleId]
  );
  // Fetch the full schedule detail on select so ticket types + add-ons are
  // always available (the list endpoint may not include add-ons).
  const [scheduleDetail, setScheduleDetail] = useState(null);
  React.useEffect(() => {
    if (!formData.eventScheduleId) {
      setScheduleDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForEvent()}schedule/get?eventScheduleId=${formData.eventScheduleId}`,
        });
        const obj = (res && res.data && res.data.data) || null;
        if (!cancelled) setScheduleDetail(obj);
      } catch (e) {
        if (!cancelled) setScheduleDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.eventScheduleId]);

  const scheduleTickets =
    scheduleDetail && Array.isArray(scheduleDetail.eventPricingList) && scheduleDetail.eventPricingList.length
      ? scheduleDetail.eventPricingList
      : selectedSchedule?.eventPricingList || [];
  const scheduleAddons =
    scheduleDetail && Array.isArray(scheduleDetail.addonsPricingList) && scheduleDetail.addonsPricingList.length
      ? scheduleDetail.addonsPricingList
      : selectedSchedule?.addonsPricingList || [];

  const toggleInArray = (arr, val) =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      dispatch(setSectionLoading(true));
      await getPromotions();
      await getEventsList();
      dispatch(clearLoadingData());
      setLoading(false);
    })();
  }, []);

  const getPromotions = async () => {
    const config = {
      method: "get",
      url: `${getUrlForEvent()}commissionoffer/list?getall=true`,
    };
    dispatch(setSectionLoading(true));
    const response = await axiosInstance(config);
    dispatch(setSectionLoading(false));
    if (response.status === 200) {
      if (Array.isArray(response.data.data)) {
        setPromotionsList(response.data.data);
      }
    }
  };

  const getEventsList = async () => {
    const config = {
      method: "get",
      url: `${getUrlForEvent()}groupScheduledEvent?filterOfDate=upcoming`,
    };
    const response = await axiosInstance(config);
    if (response.status === 200 && response.data.data) {
      const data = response.data.data;
      const uniqueEvents = [];
      const seen = new Set();
      const items = Array.isArray(data) ? data : Object.values(data).flat();
      items.forEach((item) => {
        const id = item.instanceId || item._id;
        const name = item.eventName || item.name;
        if (id && !seen.has(id)) {
          seen.add(id);
          uniqueEvents.push({
            _id: id,
            eventName: name,
            eventPhoto: item.eventPhoto || "",
            eventDate: item.eventDate || "",
            location: item.location || {},
            eventPricingList: Array.isArray(item.eventPricingList) ? item.eventPricingList : [],
            addonsPricingList: Array.isArray(item.addonsPricingList) ? item.addonsPricingList : [],
          });
        }
      });
      setEventsList(uniqueEvents);
    }
  };

  const getSellersList = async () => {
    const config = {
      method: "get",
      url: `${getUrlForEvent()}seller/list?getall=true`,
    };
    const response = await axiosInstance(config);
    if (response.status === 200 && Array.isArray(response.data.data)) {
      // Show ALL of the organizer's sellers (same set as the My Sellers page).
      // Sellers without an email can't be invited (no link to send), so they're
      // rendered disabled with a hint rather than silently hidden.
      setSellersList(response.data.data);
    }
  };

  const handleInviteSellers = async (offerId) => {
    setInviteOfferId(offerId);
    setSelectedSellerIds([]);
    setInviteResult(null);
    setAddMode("invite");
    setEnrolledSellerIds([]);
    if (sellersList.length === 0) getSellersList();
    setInviteDrawerOpen(true);
    // Who's already on this campaign → show them ticked + locked so we don't
    // re-invite. Best-effort; a failure just means no pre-ticks.
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForEvent()}commissionoffer/${offerId}/performance` });
      if (res?.data?.statusCode === 200) {
        setEnrolledSellerIds((res.data.data.sellers || []).map((s) => String(s.sellerId)));
      }
    } catch (e) {
      /* ignore */
    }
  };

  const toggleSellerSelection = (sellerId) => {
    setSelectedSellerIds((prev) =>
      prev.includes(sellerId) ? prev.filter((id) => id !== sellerId) : [...prev, sellerId]
    );
  };

  const handleSendInvites = async () => {
    if (selectedSellerIds.length === 0) return;
    setSendingInvites(true);
    setInviteResult(null);
    const url =
      addMode === "managed"
        ? `${getUrlForEvent()}commissionoffer/managed-add`
        : `${getUrlForEvent()}commissionoffer/invite`;
    const response = await axiosInstance({
      method: "post",
      url,
      data: { offerId: inviteOfferId, sellerIds: selectedSellerIds },
    });
    setSendingInvites(false);
    if (response.status === 200 && response.data.statusCode === 200) {
      setInviteResult({ success: true, mode: addMode, data: response.data.data?.results || [] });
      // Managed-add creates channels immediately — refresh enrolled counts.
      if (addMode === "managed") getPromotions();
    } else {
      setInviteResult({ success: false });
    }
  };

  const handleToggleStatus = async (offer) => {
    const newStatus = offer.status === "active" ? "paused" : "active";
    const config = {
      method: "post",
      url: `${getUrlForEvent()}commissionoffer/update/${offer._id}`,
      data: { status: newStatus },
    };
    await axiosInstance(config);
    await getPromotions();
  };

  const clearForm = () => {
    setFormData(defaultFormData);
    setTiers([{ from: 1, commissionPerTicket: 1 }]);
    setAddonForm(defaultAddonForm);
    setAddonTiers([{ from: 1, commissionPerTicket: 1 }]);
    setFormErrors({});
    setIsEdit(null);
  };

  const validateData = () => {
    const errors = {};
    if (!formData.eventScheduleId) {
      errors.eventScheduleId = t("errors.required");
    }
    if (!formData.label || formData.label.trim() === "") {
      errors.label = t("errors.required");
    }

    // Tracking-only campaigns carry no commission model, so skip all of it.
    if (formData.isCommissionApplicable !== false) {
      if (formData.commissionModelType === "flat") {
        if (formData.flatValue <= 0) {
          errors.flatValue = t("errors.required");
        }
      } else {
        for (let i = 0; i < tiers.length; i++) {
          if (tiers[i].from === undefined || isNaN(tiers[i].from)) {
            errors[`tier_from_${i}`] = t("errors.required");
          }
          if (tiers[i].commissionPerTicket === undefined || isNaN(tiers[i].commissionPerTicket)) {
            errors[`tier_rate_${i}`] = t("errors.required");
          }
          if (i < tiers.length - 1) {
            if (tiers[i].to === undefined || tiers[i].to === "" || isNaN(tiers[i].to)) {
              errors[`tier_to_${i}`] = t("errors.required");
            }
          }
        }
      }

      if (formData.isAddBonus) {
        if (formData.bonusThreshold <= 0) {
          errors.bonusThreshold = t("errors.required");
        }
        if (formData.bonusAmount <= 0) {
          errors.bonusAmount = t("errors.required");
        }
      }
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      errors.endDate = t("myPromotions.endDateBeforeStart");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateData()) return;

    let finalTiers = tiers.map((t, i) => {
      let returnval = {
        ...t,
        from: parseFloat(t.from),
        ...(t.to ? { to: parseFloat(t.to) } : {}),
        commissionPerTicket: parseFloat(t.commissionPerTicket),
      };
      if (tiers.length - 1 === i) {
        delete returnval.to;
      }
      return returnval;
    });

    const finalAddonTiers = addonTiers.map((t, i) => {
      let r = { ...t, from: parseFloat(t.from), ...(t.to ? { to: parseFloat(t.to) } : {}), commissionPerTicket: parseFloat(t.commissionPerTicket) };
      if (addonTiers.length - 1 === i) delete r.to;
      return r;
    });

    const payload = {
      eventScheduleId: formData.eventScheduleId,
      label: formData.label,
      isCommissionApplicable: formData.isCommissionApplicable !== false,
      commissionModelType: formData.commissionModelType,
      unitType: formData.unitType,
      tiers: finalTiers,
      isAddBonus: formData.isAddBonus,
      bonusThreshold: parseInt(formData.bonusThreshold),
      bonusAmount: parseFloat(formData.bonusAmount),
      maxSellers: parseInt(formData.maxSellers) || 0,
      status: formData.status,
      ...(formData.startDate ? { startDate: new Date(formData.startDate).toISOString() } : {}),
      ...(formData.endDate ? { endDate: new Date(formData.endDate).toISOString() } : {}),
      // Storefront filter + add-on commission
      allowedTicketTypes: formData.allowedTicketTypes || [],
      applyToAddons: !!formData.applyToAddons,
      allowedAddonIds: formData.applyToAddons ? (formData.allowedAddonIds || []) : [],
      addonCommission: formData.applyToAddons
        ? (formData.addonSameAsTickets
          ? { sameAsTickets: true }
          : {
            sameAsTickets: false,
            commissionModelType: addonForm.commissionModelType,
            unitType: addonForm.unitType,
            tiers: finalAddonTiers,
            isAddBonus: addonForm.isAddBonus,
            bonusThreshold: parseInt(addonForm.bonusThreshold) || 0,
            bonusAmount: parseFloat(addonForm.bonusAmount) || 0,
          })
        : { sameAsTickets: true },
    };

    dispatch(setGlobalLoading(true));
    dispatch(setActionLoading(true));

    let config;
    if (isEdit) {
      config = {
        method: "post",
        url: `${getUrlForEvent()}commissionoffer/update/${isEdit}`,
        data: payload,
      };
    } else {
      config = {
        method: "post",
        url: `${getUrlForEvent()}commissionoffer/add`,
        data: payload,
      };
    }

    const response = await axiosInstance(config);
    dispatch(setGlobalLoading(false));
    dispatch(setActionLoading(false));

    if (response.status === 200 && response.data.statusCode === 200) {
      clearForm();
      setDrawerOpen(false);
      await getPromotions();
    }
  };

  const handleEditOffer = (offer) => {
    setIsEdit(offer._id);
    const flatVal = offer.commissionModelType === "flat" && offer.tiers?.length > 0
      ? offer.tiers[0].commissionPerTicket
      : 0;
    setFormData({
      eventScheduleId: offer.eventScheduleId,
      label: offer.label || "",
      isCommissionApplicable: offer.isCommissionApplicable !== false,
      commissionModelType: offer.commissionModelType || "flat",
      unitType: offer.unitType || "flatFeePerTicket",
      flatValue: flatVal,
      tiers: offer.tiers || [{ from: 1, commissionPerTicket: 1 }],
      isAddBonus: offer.isAddBonus || false,
      bonusThreshold: offer.bonusThreshold || 0,
      bonusAmount: offer.bonusAmount || 0,
      maxSellers: offer.maxSellers || 0,
      status: offer.status || "active",
      // datetime-local expects LOCAL time. toISOString() is UTC, so shift by the
      // local offset — otherwise the shown/stored time drifts by the tz offset.
      startDate: toLocalDatetimeInput(offer.startDate),
      endDate: toLocalDatetimeInput(offer.endDate),
      allowedTicketTypes: offer.allowedTicketTypes || [],
      applyToAddons: offer.applyToAddons || false,
      allowedAddonIds: offer.allowedAddonIds || [],
      addonSameAsTickets: offer.addonCommission ? offer.addonCommission.sameAsTickets !== false : true,
    });
    setTiers(offer.tiers?.length > 0 ? offer.tiers : [{ from: 1, commissionPerTicket: 1 }]);
    const ac = offer.addonCommission || {};
    setAddonForm({
      commissionModelType: ac.commissionModelType || "flat",
      unitType: ac.unitType || "flatFeePerTicket",
      flatValue: ac.commissionModelType === "flat" && ac.tiers?.length > 0 ? ac.tiers[0].commissionPerTicket : 0,
      isAddBonus: ac.isAddBonus || false,
      bonusThreshold: ac.bonusThreshold || 0,
      bonusAmount: ac.bonusAmount || 0,
    });
    setAddonTiers(ac.tiers?.length > 0 ? ac.tiers : [{ from: 1, commissionPerTicket: 1 }]);
    setDrawerOpen(true);
  };

  // ─── Tier management ───
  const addTier = () => {
    const DEFAULT_TIER_RANGE = 9;
    const lastTier = tiers[tiers.length - 1];
    if (lastTier.from === undefined || isNaN(lastTier.from) || lastTier.commissionPerTicket === undefined || isNaN(lastTier.commissionPerTicket)) return;
    let updatedTiers = [...tiers];
    if (lastTier.to === undefined || lastTier.to === "" || isNaN(lastTier.to)) {
      const autoTo = parseFloat(lastTier.from) + DEFAULT_TIER_RANGE;
      updatedTiers[tiers.length - 1] = { ...lastTier, to: autoTo };
    }
    const newFrom = parseFloat(updatedTiers[tiers.length - 1].to) + 1;
    setTiers([...updatedTiers, { from: parseFloat(newFrom), to: undefined, commissionPerTicket: 0 }]);
  };

  const removeTier = (index) => {
    const newTiers = [...tiers.filter((_, i) => i !== index)];
    newTiers[newTiers.length - 1] = { ...newTiers[newTiers.length - 1], to: "" };
    setTiers(newTiers);
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    if (field === 'from' && formData.commissionModelType === "thresholdBased") {
      for (let i = 1; i < newTiers.length; i++) {
        newTiers[i - 1] = { ...newTiers[i - 1], to: parseFloat(newTiers[i].from) - 1 };
      }
    } else {
      newTiers[newTiers.length - 1] = { ...newTiers[newTiers.length - 1], to: undefined };
    }
    setTiers(newTiers);
  };

  const filteredEvents = React.useMemo(() => {
    if (!eventQuery || eventQuery.trim() === "") return eventsList;
    const q = eventQuery.toLowerCase();
    return eventsList.filter((ev) =>
      (ev.eventName || "").toLowerCase().includes(q) ||
      (ev.location?.address || "").toLowerCase().includes(q) ||
      (ev.location?.city || "").toLowerCase().includes(q)
    );
  }, [eventsList, eventQuery]);

  const getCommissionSummary = (offer) => {
    const unitLabel = offer.unitType === "percentageOnTicketPrice" ? "%" : "€";
    if (offer.commissionModelType === "flat" && offer.tiers?.length > 0) {
      return `${offer.tiers[0].commissionPerTicket}${unitLabel} ${t("myPromotions.perTicket")}`;
    } else if (offer.commissionModelType === "tiered") {
      return `${offer.tiers?.length || 0} ${t("myEvents.tiers").toLowerCase()}`;
    } else if (offer.commissionModelType === "thresholdBased") {
      return `${offer.tiers?.length || 0} ${t("myEvents.thresholds").toLowerCase()}`;
    }
    return "";
  };

  const getCampaignDateLabel = (offer) => {
    if (!offer.startDate && !offer.endDate) return null;
    const fmt = (d) => new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" });
    if (offer.startDate && offer.endDate) return `${fmt(offer.startDate)} - ${fmt(offer.endDate)}`;
    if (offer.startDate) return `${t("myPromotions.from")} ${fmt(offer.startDate)}`;
    if (offer.endDate) return `${t("myPromotions.until")} ${fmt(offer.endDate)}`;
    return null;
  };

  const PerfStat = ({ label, value, accent }) => (
    <div>
      <div className={`text-base font-semibold ${accent ? "text-blue-700" : "text-gray-900"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    </div>
  );

  const renderBreakdown = (s) => (
    <div className="px-4 pt-2 overflow-x-auto">
      <table className="min-w-full text-xs text-left border border-gray-100 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="px-3 py-2 font-medium">Tier</th>
            <th className="px-3 py-2 font-medium">Rate</th>
            <th className="px-3 py-2 font-medium">Tickets Sold</th>
            <th className="px-3 py-2 font-medium">Revenue</th>
            <th className="px-3 py-2 font-medium">Commission</th>
          </tr>
        </thead>
        <tbody>
          {(s.bands || []).map((b, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="px-3 py-2">{bandRange(b)}</td>
              <td className="px-3 py-2">{rateLabel(b)}</td>
              <td className="px-3 py-2">{b.count}</td>
              <td className="px-3 py-2">{money(b.revenue)}</td>
              <td className="px-3 py-2">{money(b.subtotal)}</td>
            </tr>
          ))}
          <tr className="border-t border-gray-200 bg-gray-50 font-medium">
            <td className="px-3 py-2" colSpan={2}>Total (tickets)</td>
            <td className="px-3 py-2">{s.ticketsSold}</td>
            <td className="px-3 py-2">{money(s.ticketRevenue)}</td>
            <td className="px-3 py-2">{money(s.ticketCommission)}</td>
          </tr>
          {s.applyToAddons && s.addonsSold > 0 &&
            (s.addonBands || []).map((b, i) => (
              <tr key={`a${i}`} className="border-t border-gray-100 text-gray-500">
                <td className="px-3 py-2">Add-on {bandRange(b)}</td>
                <td className="px-3 py-2">{rateLabel(b)}</td>
                <td className="px-3 py-2">{b.count}</td>
                <td className="px-3 py-2">{money(b.revenue)}</td>
                <td className="px-3 py-2">{money(b.subtotal)}</td>
              </tr>
            ))}
          {s.bonusEarned > 0 && (
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2" colSpan={4}>Bonus Earned</td>
              <td className="px-3 py-2">{money(s.bonusEarned)}</td>
            </tr>
          )}
          <tr className="border-t border-gray-200 bg-blue-50 font-semibold text-blue-800">
            <td className="px-3 py-2" colSpan={4}>Total Payout</td>
            <td className="px-3 py-2">{money(s.commission)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderPerformance = () => {
    const c = perfData?.campaign;
    // Same "can this campaign take on sellers?" rule as the cards, resolved from
    // the campaign in the list (which carries status + endDate).
    const perfOffer = promotionsList.find((o) => o._id === perfOfferId);
    const perfEnded = !!(perfOffer && perfOffer.endDate) && new Date(perfOffer.endDate) < new Date();
    const perfActionable = !!perfOffer && perfOffer.status === "active" && !perfEnded;
    const totals = perfData?.totals;
    const sellersArr = perfData?.sellers || [];
    const campaignHasAddons = !!(c && c.applyToAddons) || sellersArr.some((s) => s.applyToAddons);
    const totalOutstanding = sellersArr.reduce((sum, s) => sum + (s.outstanding || 0), 0);
    const perfTime = c && c.eventDate
      ? `${new Date(c.eventDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${c.eventEndDate ? ` – ${new Date(c.eventEndDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}`
      : "";
    const perfLoc = c && c.location ? [c.location.addressName, c.location.city].filter(Boolean).join(", ") : "";
    return (
      <div className="flex flex-col gap-4">
        <button onClick={closePerformance} className="self-start text-sm text-gray-500 hover:text-gray-900">
          ← {t("inputFieldsTitle.back") || "Back to campaigns"}
        </button>

        {perfLoading ? (
          <div className="text-center text-gray-400 py-16">Loading…</div>
        ) : !perfData ? (
          <div className="text-center text-gray-400 py-16">{t("myPromotions.noData") || "No performance data yet."}</div>
        ) : (
          <>
            {/* Campaign header */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-4">
              {/* Campaign name — bold blue */}
              <h2 className="font-bold text-blue-700 text-xl leading-tight">{c.label || t("myPromotions.campaign")}</h2>

              {/* Event photo + name + date/time/place */}
              <div className="flex gap-4 items-center">
                <img
                  src={c.eventImage || noEventImage}
                  alt=""
                  className="w-24 h-20 rounded-xl object-cover shrink-0 border border-gray-100"
                />
                <div className="min-w-0 flex flex-col gap-1">
                  <h5 className="font-semibold text-gray-900 truncate">{c.eventName}</h5>
                  {c.eventDate && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <CalendarDaysIcon className="size-3.5 shrink-0" />
                      {new Date(c.eventDate).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                      {perfTime ? <span className="ml-1">· {perfTime}</span> : null}
                    </div>
                  )}
                  {perfLoc && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <MapPinIcon className="size-3.5 shrink-0" /><span className="truncate">{perfLoc}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stat pills */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 border-t border-gray-100 pt-4">
                {[
                  { label: t("myPromotions.enrolled"), value: totals.enrolled || 0 },
                  { label: t("myEvents.ticketsSold") || "Tickets sold", value: totals.ticketsSold || 0 },
                  { label: t("myEvents.addons") || "Add-ons", value: campaignHasAddons ? (totals.addonsSold || 0) : "NA" },
                  { label: t("myPromotions.revenue") || "Revenue", value: money(totals.revenue) },
                  { label: t("myPromotions.commissionToPay") || "To pay", value: money(totalOutstanding), accent: true },
                  { label: t("myPromotions.costPerTicket") || "Cost/ticket", value: money(totals.costPerTicket) },
                ].map((p, i) => (
                  <div key={i} className="rounded-xl bg-gray-50 py-2 text-center">
                    <div className={`text-base font-semibold ${p.accent ? "text-blue-700" : "text-gray-900"}`}>{p.value}</div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">{p.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sellers grid */}
            <div className="flex flex-col gap-4 mt-2">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  {t("myPromotions.sellers") || "Sellers"} ({perfData.sellers.length})
                </h4>
                <button
                  type="button"
                  onClick={syncStripePayouts}
                  disabled={syncingPayouts}
                  title={t("myPromotions.syncStripePayoutsHint") || "Pull any completed Stripe payouts that aren't showing yet."}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <BanknotesIcon className="size-4 text-gray-500" />
                  {syncingPayouts ? (t("myPromotions.syncing") || "Syncing…") : (t("myPromotions.syncStripePayouts") || "Sync Stripe payouts")}
                </button>
              </div>
              {perfData.sellers.length === 0 ? (
                <div className="text-center text-gray-400 py-8 bg-white rounded-xl border border-gray-200">
                  {t("myPromotions.noSellersYet") || "No sellers on this campaign yet."}
                </div>
              ) : (
                <div className="flex flex-col gap-5 pb-6">
                  {perfData.sellers.map((s) => {
                    const initials = (s.name || s.sellerReferenceId || "S").slice(0, 2).toUpperCase();
                    return (
                      <div key={s.sellerId} className={`relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden pb-4 ${s.disabled ? "opacity-60" : ""}`}>
                        {/* Header: avatar + name + top-right actions */}
                        <div className="p-4 flex items-start gap-3">
                          {s.profilePic ? (
                            <img src={s.profilePic} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold shrink-0">{initials}</div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 truncate">
                              {s.name || s.sellerReferenceId || "Seller"}
                              {s.disabled && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{t("myPromotions.disabled") || "disabled"}</span>}
                              {s.unattributed && <span className="ml-2 text-[11px] text-orange-500">{t("myPromotions.unattributed") || "unattributed"}</span>}
                              {s.isCommissionApplicable === false && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{t("myPromotions.trackingOnly") || "Tracking only"}</span>}
                            </div>
                            {s.email && <div className="text-xs text-gray-400 truncate">{s.email}</div>}
                          </div>

                          {/* Two peer actions: Pay promoter (primary, black) + Actions */}
                          <div className="flex items-center gap-2 shrink-0">
                            {s.isCommissionApplicable !== false && (
                              <PayMenu
                                label={t("myPromotions.payPromoter") || "Pay promoter"}
                                hasStripe={!!s.hasConnectedStripe}
                                canPayOffline={!s.canAutoPayout && !s.hasConnectedStripe}
                                disabled={!(s.outstanding > 0)}
                                variant="primary"
                                onOnline={() => payViaStripe(s.sellerId, s.outstanding, "organizer")}
                                onOffline={() => markCommissionPaid(s.sellerId, s.outstanding, "organizer")}
                                t={t}
                              />
                            )}
                            <Menu as="div" className="relative">
                              <MenuButton className="text-sm px-3 py-1.5 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1">
                                {t("myPromotions.actions") || "Actions"}
                                <ChevronDownIcon className="size-4" />
                              </MenuButton>
                              <MenuItems anchor="bottom end" className="z-20 mt-1 w-60 rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none py-1 text-sm">
                                {s.trackedLink && (
                                  <MenuItem>
                                    <button
                                      disabled={!perfActionable}
                                      onClick={() => { if (!perfActionable) return; navigator.clipboard.writeText(s.trackedLink); addSuccessToast && addSuccessToast(t("myEvents.copied")); }}
                                      title={!perfActionable ? (perfEnded ? t("myPromotions.endedHint") : t("myPromotions.pausedHint")) : ""}
                                      className={`w-full text-left px-4 py-2 text-gray-700 data-[focus]:bg-gray-50 ${!perfActionable ? "opacity-40 cursor-not-allowed" : ""}`}
                                    >
                                      {t("myEvents.copySellingLink") || "Copy selling link"}
                                    </button>
                                  </MenuItem>
                                )}
                                <MenuItem>
                                  <button
                                    disabled={!perfActionable}
                                    onClick={() => { if (perfActionable) sendSellerDashboardLink(s.sellerId); }}
                                    title={!perfActionable ? (perfEnded ? t("myPromotions.endedHint") : t("myPromotions.pausedHint")) : ""}
                                    className={`w-full text-left px-4 py-2 text-gray-700 data-[focus]:bg-gray-50 ${!perfActionable ? "opacity-40 cursor-not-allowed" : ""}`}
                                  >
                                    {t("myEvents.sendTrackLink") || "Send 'Track my sales' link"}
                                  </button>
                                </MenuItem>
                                {!s.unattributed && (
                                  <MenuItem>
                                    <button
                                      disabled={!perfActionable}
                                      onClick={() => { if (perfActionable) openBannerDialog(s); }}
                                      title={!perfActionable ? (perfEnded ? t("myPromotions.endedHint") : t("myPromotions.pausedHint")) : ""}
                                      className={`w-full text-left px-4 py-2 text-gray-700 data-[focus]:bg-gray-50 ${!perfActionable ? "opacity-40 cursor-not-allowed" : ""}`}
                                    >
                                      {s.bannerImage
                                        ? (t("myPromotions.changeBanner") || "Change customized banner")
                                        : (t("myPromotions.addBanner") || "Add customized banner")}
                                    </button>
                                  </MenuItem>
                                )}
                                {!s.unattributed && (
                                  <MenuItem>
                                    {(() => {
                                      const blockEnable = s.disabled && !perfActionable;
                                      return (
                                        <button
                                          onClick={() => !blockEnable && toggleDisableSeller(s.sellerId, !s.disabled)}
                                          disabled={blockEnable}
                                          title={blockEnable
                                            ? (perfEnded
                                              ? (t("myPromotions.endedHint") || "This campaign has ended — edit it to reactivate first.")
                                              : (t("myPromotions.pausedHint") || "This campaign is paused — reactivate it first."))
                                            : ""}
                                          className={`w-full text-left px-4 py-2 text-gray-700 data-[focus]:bg-gray-50 ${blockEnable ? "opacity-40 cursor-not-allowed" : ""}`}
                                        >
                                          {s.disabled ? (t("myPromotions.enableSeller") || "Enable seller") : (t("myPromotions.disableSeller") || "Disable seller")}
                                        </button>
                                      );
                                    })()}
                                  </MenuItem>
                                )}
                              </MenuItems>
                            </Menu>
                          </div>
                        </div>

                        {/* Stat pills — clickable for details (one at a time) */}
                        <div className="px-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[
                            { pill: "tickets", label: t("myEvents.ticketsSold") || "Tickets sold", value: s.ticketsSold },
                            { pill: "addons", label: t("myEvents.addons") || "Add-ons", value: s.addonsSold },
                            { pill: s.isCommissionApplicable === false ? null : "commission", label: t("myPromotions.commissionToPay") || "To pay", value: s.isCommissionApplicable === false ? "—" : money(s.outstanding), accent: s.isCommissionApplicable !== false },
                            { pill: null, label: t("myPromotions.costPerTicket") || "Cost/ticket", value: s.isCommissionApplicable === false ? "—" : money(s.costPerTicket) },
                          ].map((p, i) => {
                            const active = p.pill && sellerDetail && sellerDetail.id === s.sellerId && sellerDetail.pill === p.pill;
                            const cls = `rounded-xl py-2 text-center transition ${p.pill ? "cursor-pointer hover:bg-gray-100" : ""} ${active ? "bg-blue-50 ring-1 ring-blue-300" : "bg-gray-50"}`;
                            const inner = (
                              <>
                                <div className={`text-sm font-semibold ${p.accent ? "text-blue-700" : "text-gray-900"}`}>{p.value}</div>
                                <div className="text-[10px] uppercase tracking-wide text-gray-400">{p.label}</div>
                              </>
                            );
                            return p.pill ? (
                              <button key={i} type="button" onClick={() => toggleSellerDetail(s.sellerId, p.pill)} className={`${cls} w-full`}>{inner}</button>
                            ) : (
                              <div key={i} className={cls}>{inner}</div>
                            );
                          })}
                        </div>

                        {/* Tracking-only note */}
                        {s.isCommissionApplicable === false && (
                          <div className="px-4 pt-2 text-xs text-gray-400">
                            {t("myPromotions.trackingOnlyHint") || "Tracking only — sales are attributed to this channel, no commission or payout."}
                          </div>
                        )}

                        {/* Pending seller confirmation — visible when an offline payment awaits seller's receipt */}
                        {s.isCommissionApplicable !== false && s.pendingConfirmation > 0 && (
                          <div className="px-4 pt-3">
                            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                              <svg className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" /></svg>
                              <div>
                                <p className="text-sm font-medium text-amber-900">
                                  {money(s.pendingConfirmation)} {t("myPromotions.awaitingSellerConfirmation") || "awaiting seller confirmation"}
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                  {t("myPromotions.awaitingSellerConfirmationHint") || "The seller needs to confirm they received this payment. It will auto-confirm in 14 days."}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Pending payout requests — actionable (Pay / Decline) */}
                        {s.isCommissionApplicable !== false && (s.payoutRequests || []).some((r) => r.status === "pending") && (
                          <div className="px-4 pt-3">
                            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t("myPromotions.pendingRequests") || "Pending requests"}</div>
                            <div className="rounded-xl border border-amber-100 bg-amber-50/40 overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-amber-50 text-[11px] uppercase tracking-wide text-amber-700/70">
                                  <tr>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.reqRequested") || "Requested"}</th>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.reqAmount") || "Amount"}</th>
                                    <th className="text-right px-3 py-2 font-medium">{t("myPromotions.reqAction") || "Action"}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-amber-100">
                                  {s.payoutRequests.filter((r) => r.status === "pending").map((r) => (
                                    <tr key={r._id}>
                                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.requestedAt ? new Date(r.requestedAt).toLocaleDateString() : "—"}</td>
                                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{money(r.amount)}</td>
                                      <td className="px-3 py-2 text-right">
                                        <div className="inline-flex items-center gap-2 justify-end">
                                          <PayMenu
                                            label={`${t("myPromotions.pay") || "Pay"} ${money(r.amount)}`}
                                            hasStripe={!!s.hasConnectedStripe}
                                            canPayOffline={!s.canAutoPayout && !s.hasConnectedStripe}
                                            disabled={false}
                                            variant="row"
                                            onOnline={() => payViaStripe(s.sellerId, r.amount, "seller_request")}
                                            onOffline={() => markCommissionPaid(s.sellerId, r.amount, "seller_request")}
                                            t={t}
                                          />
                                          <button onClick={() => declinePayoutRequest(r._id)} className="text-xs text-red-500 hover:underline">{t("myPromotions.decline") || "Decline"}</button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Payments history — every settlement recorded (incl. organizer-initiated) */}
                        {s.isCommissionApplicable !== false && (s.settlements || []).length > 0 && (
                          <div className="px-4 pt-3">
                            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t("myPromotions.paymentsHistory") || "Payments history"}</div>
                            <div className="rounded-xl border border-gray-100 overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
                                  <tr>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.initiatedBy") || "Initiated by"}</th>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.initiationDate") || "Initiation date"}</th>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.reqAmount") || "Amount"}</th>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.dateOfPayment") || "Date of payment"}</th>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.paymentMethod") || "Payment method"}</th>
                                    <th className="text-left px-3 py-2 font-medium">{t("myPromotions.paymentStatus") || "Status"}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {s.settlements.map((p) => (
                                    <tr key={p._id}>
                                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{p.initiatedBy === "seller_request" ? (t("myPromotions.initPromoter") || "Promoter") : (t("myPromotions.initOrganizer") || "Organizer")}</td>
                                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{p.initiatedAt ? new Date(p.initiatedAt).toLocaleDateString() : "—"}</td>
                                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{money(p.amount)}</td>
                                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{p.settledAt ? new Date(p.settledAt).toLocaleDateString() : "—"}</td>
                                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{p.method === "stripe" ? (t("myPromotions.methodPaidOnline") || "Paid online") : (t("myPromotions.methodOffline") || "Offline")}</td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        {p.status === "pending_confirmation" ? (
                                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">{t("myPromotions.statusPendingConfirmation") || "Awaiting confirmation"}</span>
                                        ) : p.status === "disputed" ? (
                                          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">{t("myPromotions.statusDisputed") || "Disputed"}</span>
                                        ) : (
                                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">{t("myPromotions.statusConfirmed") || "Confirmed"}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Pill detail: tickets by type / add-ons / commission calc */}
                        {sellerDetail && sellerDetail.id === s.sellerId && sellerDetail.pill === "tickets" && (
                          <div className="px-4 pt-2">
                            {(s.ticketTypeSplit || []).length ? (
                              <ul className="rounded-xl border border-gray-100 divide-y divide-gray-100 text-sm">
                                {s.ticketTypeSplit.map((tt, i) => (
                                  <li key={i} className="flex items-center justify-between px-3 py-2">
                                    <span className="text-gray-700">{tt.ticketType}{tt.price ? <span className="text-gray-400"> (€{Number(tt.price).toFixed(2)})</span> : null}</span>
                                    <span className="font-medium text-gray-900">{tt.tickets} · €{Number(tt.revenue).toFixed(2)}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (<p className="text-xs text-gray-400">{t("myPromotions.noTicketSales") || "No ticket sales yet."}</p>)}
                          </div>
                        )}
                        {sellerDetail && sellerDetail.id === s.sellerId && sellerDetail.pill === "addons" && (
                          <div className="px-4 pt-2">
                            {(s.addonSplit || []).length ? (
                              <ul className="rounded-xl border border-gray-100 divide-y divide-gray-100 text-sm">
                                {s.addonSplit.map((ad, i) => (
                                  <li key={i} className="flex items-center justify-between px-3 py-2">
                                    <span className="text-gray-700">{ad.addonName}{ad.price ? <span className="text-gray-400"> (€{Number(ad.price).toFixed(2)})</span> : null}</span>
                                    <span className="font-medium text-gray-900">{ad.units} · €{Number(ad.revenue).toFixed(2)}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (<p className="text-xs text-gray-400">{t("myPromotions.noAddonSales") || "No add-ons sold."}</p>)}
                          </div>
                        )}
                        {sellerDetail && sellerDetail.id === s.sellerId && sellerDetail.pill === "commission" && (
                          <div className="px-4 pt-2">
                            <div className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600 mb-2">
                              {t("myPromotions.commission") || "Commission"}: <span className="font-semibold text-gray-900">{money(s.commission)}</span>
                              {" · "}{t("myPromotions.paid") || "Paid"}: <span className="font-semibold text-gray-900">{money(s.paidAmount)}</span>
                              {s.pendingConfirmation > 0 && (<>{" · "}{t("myPromotions.pendingConfirmationLabel") || "Pending"}: <span className="font-semibold text-amber-700">{money(s.pendingConfirmation)}</span></>)}
                              {" · "}{t("myPromotions.outstanding") || "Outstanding"}: <span className="font-semibold text-blue-700">{money(s.outstanding)}</span>
                            </div>
                            {renderBreakdown(s)}
                          </div>
                        )}

                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Invite tracking — shows status of each invite sent for this campaign */}
            {inviteTrackingData.length > 0 && (
              <div className="flex flex-col gap-3 mt-4">
                <h4 className="text-sm font-semibold text-gray-700">
                  {t("myPromotions.inviteTracking") || "Invite tracking"} ({inviteTrackingData.length})
                </h4>
                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium">{t("myPromotions.inviteSeller") || "Seller"}</th>
                        <th className="text-left px-4 py-2.5 font-medium">{t("myPromotions.inviteEmail") || "Email"}</th>
                        <th className="text-left px-4 py-2.5 font-medium">{t("myPromotions.inviteStatus") || "Status"}</th>
                        <th className="text-left px-4 py-2.5 font-medium">{t("myPromotions.inviteSentOn") || "Sent on"}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {inviteTrackingData.map((inv) => {
                        const statusColors = {
                          invited: "bg-blue-50 text-blue-700",
                          accepted: "bg-green-50 text-green-700",
                          used: "bg-green-50 text-green-700",
                          declined: "bg-red-50 text-red-600",
                          expired: "bg-gray-100 text-gray-500",
                        };
                        const statusLabel = {
                          invited: t("myPromotions.statusInvited") || "Invited",
                          accepted: t("myPromotions.statusAccepted") || "Accepted",
                          used: t("myPromotions.statusUsed") || "Used",
                          declined: t("myPromotions.statusDeclined") || "Declined",
                          expired: t("myPromotions.statusExpired") || "Expired",
                        };
                        const st = inv.status || "invited";
                        return (
                          <tr key={inv._id}>
                            <td className="px-4 py-2.5 text-gray-900 font-medium whitespace-nowrap">{inv.sellerName || "—"}</td>
                            <td className="px-4 py-2.5 text-gray-600 truncate max-w-[200px]">{inv.email || "—"}</td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[st] || "bg-gray-100 text-gray-500"}`}>
                                {statusLabel[st] || st}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {inviteTrackingLoading && (
              <div className="text-center text-gray-400 py-4 text-sm">{t("myPromotions.loadingInvites") || "Loading invites…"}</div>
            )}
          </>
        )}

        {/* Customized banner dialog: the image a buyer sees when they arrive
            through THIS seller's link (stored on the seller's sales channel). */}
        <TwConfirmDialog
          dialogOpen={!!bannerSeller}
          dialogType=""
          dialogTitle={t("myEvents.addBannerImageTitle") || "Seller’s Event Banner"}
          dialogMessage={t("myEvents.addBannerImageSubTitle") || "This banner appears to customers when they purchase through this seller’s link."}
          isConfirmBtn={false}
          isCloseBtn={true}
          btnCloseText={t("inputFieldsTitle.close") || "Close"}
          onClose={() => { setBannerSeller(null); setBannerImage(""); }}
        >
          <div className="mt-4">
            {bannerSeller?.name ? (
              <p className="mb-2 text-sm font-medium text-gray-700">{bannerSeller.name}</p>
            ) : null}
            <TwDropzone
              uploadBtnText={bannerImage ? (t("myEvents.changeImage") || "Change Image") : (t("myEvents.addImage") || "Add Image")}
              label={t("myEvents.addBannerImageTitle") || "Seller’s Event Banner"}
              name={"bannerImage"}
              value={bannerImage || ""}
              isShowDeletBtn={!!bannerImage}
              onDelete={() => saveSellerBanner("")}
              onLoading={(l) => dispatch(setActionLoading(l))}
              onChange={(image) => {
                setBannerImage(image);
                // Persist immediately on upload — the dropzone has already
                // stored the file; no separate Save button needed.
                saveSellerBanner(image);
              }}
            />
          </div>
        </TwConfirmDialog>
      </div>
    );
  };

  // ─── Render ───
  return (
    <TwMainLayout
      headTitle={t("pageTitle.myPromotions")}
      // Match TwContainer's padding/width so "Create New Promotion" lines up
      // with the right edge of the promotion cards below.
      headClassName="p-5 md:px-10 w-full"
      headActions={[
        <TwButton
          className="sm:min-w-32"
          key="add"
          onClick={() => {
            clearForm();
            setDrawerOpen(true);
          }}
        >
          <PlusIcon className="size-5" />&nbsp;&nbsp;{t("myPromotions.createNewPromotion")}
        </TwButton>,
      ]}
      children={
        <TwContainer className="">
          {perfOfferId ? (
            renderPerformance()
          ) : promotionsList.length > 0 ? (
            <div className="flex flex-col gap-4">
              {promotionsList.map((offer) => {
                const isActive = offer.status === "active";
                // A campaign that's paused/closed OR past its end date can't take
                // on new/re-enabled sellers — their selling links would be dead.
                const ended = !!offer.endDate && new Date(offer.endDate) < new Date();
                const actionable = isActive && !ended;
                const unitLabel = offer.unitType === "percentageOnTicketPrice" ? "%" : "€";
                const location = offer.eventLocation || {};
                const shortLoc = [location.addressName, location.city].filter(Boolean).join(", ");
                const dt = offer.eventDate ? new Date(offer.eventDate) : null;
                const dtEnd = offer.eventEndDate ? new Date(offer.eventEndDate) : null;
                const dateLabelShort = dt ? dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
                const timeLabel = dt
                  ? `${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${dtEnd ? ` – ${dtEnd.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}`
                  : "";

                return (
                  <div
                    key={offer._id}
                    onDoubleClick={() => openPerformance(offer._id)}
                    title={t("myPromotions.doubleClickPerformance") || "Double-click to view performance"}
                    className={`bg-white rounded-2xl border p-4 flex flex-col lg:flex-row gap-4 transition-all cursor-default ${isActive ? "border-gray-200 shadow-sm" : "border-gray-200 opacity-70"}`}
                  >
                    {/* Photo (small square) */}
                    <img
                      src={offer.eventImage || noEventImage}
                      alt={offer.eventName}
                      className="w-full lg:w-28 h-40 lg:h-28 rounded-xl object-cover shrink-0 border border-gray-100"
                    />

                    {/* Middle: campaign + event info */}
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="font-bold text-gray-900 text-base leading-tight truncate">
                            {offer.label || t("myPromotions.campaign")}
                          </h4>
                          <h5 className="text-sm text-gray-600 truncate">{offer.eventName}</h5>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ended
                            ? "bg-red-100 text-red-700"
                            : isActive
                              ? "bg-green-100 text-green-700"
                              : offer.status === "paused"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-600"
                            }`}>
                            {ended ? (t("myPromotions.ended") || "Ended") : isActive ? t("myPromotions.active") : offer.status === "paused" ? t("myPromotions.paused") : offer.status}
                          </span>
                          <button
                            onClick={() => handleToggleStatus(offer)}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${isActive ? "bg-green-500" : "bg-gray-300"}`}
                            title={isActive ? t("myPromotions.clickToDisable") : t("myPromotions.clickToEnable")}
                          >
                            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${isActive ? "translate-x-4" : "translate-x-0"}`} />
                          </button>
                        </div>
                      </div>

                      {/* Date / time / location */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        {dateLabelShort && <span className="flex items-center gap-1.5"><CalendarDaysIcon className="size-3.5 shrink-0" />{dateLabelShort}</span>}
                        {timeLabel && <span className="flex items-center gap-1.5"><ClockIcon className="size-3.5 shrink-0" />{timeLabel}</span>}
                        {shortLoc && <span className="flex items-center gap-1.5 min-w-0"><MapPinIcon className="size-3.5 shrink-0" /><span className="truncate">{shortLoc}</span></span>}
                      </div>

                      {/* Commission summary */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 px-2 py-1 rounded-lg capitalize">
                          {t("myEvents." + offer.commissionModelType)}
                        </span>
                        <span className="text-sm font-semibold text-gray-700">{getCommissionSummary(offer)}</span>
                        {offer.isAddBonus && (
                          <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                            +€{offer.bonusAmount} {t("myPromotions.bonusAfter")} {offer.bonusThreshold}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: stat pills + actions */}
                    <div className="lg:w-96 shrink-0 flex flex-col gap-3 lg:border-l lg:border-gray-100 lg:pl-4">
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: t("myPromotions.enrolled"), value: offer.stats?.enrolled ?? offer.enrolledCount ?? 0 },
                          { label: t("myEvents.ticketsSold") || "Tickets", value: offer.stats?.ticketsSold ?? 0 },
                          { label: t("myEvents.addons") || "Add-ons", value: offer.stats?.addonsSold ?? 0 },
                          { label: t("myPromotions.revenue") || "Revenue", value: money(offer.stats?.revenue) },
                          { label: t("myPromotions.commission") || "Commission", value: money(offer.stats?.commission), accent: true },
                          { label: t("myPromotions.costPerTicket") || "Cost/ticket", value: money(offer.stats?.costPerTicket) },
                        ].map((p, i) => (
                          <div key={i} className="rounded-xl bg-gray-50 py-2 text-center">
                            <div className={`text-sm font-semibold ${p.accent ? "text-blue-700" : "text-gray-900"}`}>{p.value}</div>
                            <div className="text-[10px] uppercase tracking-wide text-gray-400">{p.label}</div>
                          </div>
                        ))}
                      </div>
                      {/* Three equal-weight actions sharing one outlined theme. */}
                      <div className="grid grid-cols-3 gap-2 mt-auto">
                        <button
                          onClick={() => openPerformance(offer._id)}
                          className={cardActionClass}
                        >
                          <ChartBarIcon className="size-4 shrink-0" />
                          <span className="truncate">{t("myPromotions.viewPerformance")}</span>
                        </button>
                        <button
                          onClick={() => actionable && handleInviteSellers(offer._id)}
                          disabled={!actionable}
                          className={`${cardActionClass} ${!actionable ? "opacity-40 cursor-not-allowed" : ""}`}
                          title={actionable
                            ? t("myEvents.addSellers")
                            : (ended
                              ? (t("myPromotions.endedHint") || "This campaign has ended — edit it to reactivate before adding sellers.")
                              : (t("myPromotions.pausedHint") || "This campaign is paused — reactivate it before adding sellers."))}
                        >
                          <PaperAirplaneIcon className="size-4 shrink-0" />
                          <span className="truncate">{t("myPromotions.invite")}</span>
                        </button>
                        <button
                          onClick={() => handleEditOffer(offer)}
                          className={cardActionClass}
                        >
                          <PencilSquareIcon className="size-4 shrink-0" />
                          <span className="truncate">{t("inputFieldsTitle.edit")}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : loading === false && loadingReduxState.sectionLoading !== undefined && loadingReduxState.sectionLoading === false ? (
            <div className="flex flex-col justify-center items-center gap-5 text-center h-full">
              <img className="w-40 md:w-60" src={noEventImage} alt="" />
              <div className="text-secondary text-lg font-bold mt-3">
                {t("myPromotions.noPromotionsConfigured")}
              </div>
            </div>
          ) : (
            ""
          )}

          {/* ─── Create / Edit Drawer ─── */}
          <TwDrawer
            title={isEdit ? t("myPromotions.updatePromotion") : t("myPromotions.addNewPromotion")}
            classNameDialogPanel="!max-w-lg"
            open={drawerOpen}
            onClose={() => {
              clearForm();
              setDrawerOpen(false);
            }}
            footerChildren={
              <>
                <TwButton
                  variant="secondary-outline"
                  disabled={loadingReduxState.actionLoading}
                  onClick={() => {
                    clearForm();
                    setDrawerOpen(false);
                  }}
                >
                  {t("inputFieldsTitle.cancel")}
                </TwButton>
                <TwButton
                  variant="secondary"
                  disabled={loadingReduxState.actionLoading}
                  actionLoading={loadingReduxState.actionLoading}
                  onClick={handleSave}
                >
                  {isEdit ? t("myPromotions.updatePromotion") : t("myPromotions.addNewPromotion")}
                </TwButton>
              </>
            }
          >
            <div className="flex flex-col gap-3">
              {/* Event selector */}
              <div>
                <TwLabel>{t("myPromotions.selectEvent")}</TwLabel>
                <Combobox
                  as="div"
                  value={eventsList.find((ev) => ev._id === formData.eventScheduleId) || null}
                  onChange={(ev) => {
                    setFormData((prev) => ({ ...prev, eventScheduleId: ev?._id || "" }));
                  }}
                >
                  <div className="relative mt-1">
                    <ComboboxInput
                      className={`w-full rounded-xl border ${formErrors.eventScheduleId ? "border-red-300" : "border-gray-300"} bg-white py-3 pl-3 pr-10 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                      placeholder={t("myPromotions.selectEvent")}
                      displayValue={(ev) => ev?.eventName || ""}
                      onChange={(e) => setEventQuery(e.target.value)}
                    />
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-xl px-3">
                      <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
                    </ComboboxButton>
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                      {filteredEvents.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">{t("myPromotions.selectEvent")}</div>
                      ) : (
                        filteredEvents.map((ev) => (
                          <ComboboxOption
                            key={ev._id}
                            value={ev}
                            className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white"
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={ev.eventPhoto || noEventImage}
                                alt=""
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate group-data-[selected]:font-semibold">
                                  {ev.eventName}
                                </p>
                                {ev.eventDate && (
                                  <p className="text-xs opacity-70 truncate">
                                    {new Date(ev.eventDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                    {" · "}
                                    {new Date(ev.eventDate).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                  </p>
                                )}
                                {(ev.location?.address || ev.location?.city) && (
                                  <p className="text-xs opacity-60 truncate">
                                    {[ev.location?.address, ev.location?.city].filter(Boolean).join(", ")}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-[selected]:flex group-data-[focus]:text-white">
                              <CheckIcon className="size-5" aria-hidden="true" />
                            </span>
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxOptions>
                  </div>
                </Combobox>
                {formErrors.eventScheduleId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.eventScheduleId}</p>
                )}
              </div>

              {/* Label */}
              <TwInputGroup
                label={t("myPromotions.offerLabel")}
                placeholder={t("myPromotions.offerLabelPlaceholder")}
                type="text"
                name="label"
                value={formData.label}
                onChange={(event) => {
                  setFormData((prev) => ({ ...prev, label: event.target.value }));
                }}
                errors={formErrors}
                className="!py-3 md:pl-2.5"
              />

              {/* Campaign dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <TwLabel>{t("myPromotions.startDate")}</TwLabel>
                  <input
                    type="datetime-local"
                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    value={formData.startDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div>
                  <TwLabel>{t("myPromotions.endDate")}</TwLabel>
                  <input
                    type="datetime-local"
                    className={`mt-1 w-full rounded-xl border ${formErrors.endDate ? "border-red-300" : "border-gray-300"} bg-white py-3 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                    value={formData.endDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                  {formErrors.endDate && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>
                  )}
                </div>
              </div>

              {/* Tracking-only: a shareable link with no commission/payout. */}
              <div className="border-b border-gray-900/10 pb-1"></div>
              <TwSwitch
                title={t("myPromotions.noCommissionTracking") || "No commission (tracking only)"}
                name="isCommissionApplicable"
                checked={formData.isCommissionApplicable === false}
                onChange={() => setFormData((prev) => ({ ...prev, isCommissionApplicable: prev.isCommissionApplicable === false }))}
              />
              <TwFormHelperText>
                {t("myPromotions.noCommissionHint") || "Create a shareable tracking link (e.g. for Instagram) with no commission and no payout. Sales are still attributed to this channel."}
              </TwFormHelperText>

              {formData.isCommissionApplicable !== false && (<>
              {/* Which tickets this campaign covers */}
              {scheduleTickets.length > 0 && (
                <div>
                  <TwLabel>{t("myPromotions.whichTickets", "Which tickets earn commission")}</TwLabel>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {scheduleTickets.map((tp) => {
                      const active = (formData.allowedTicketTypes || []).includes(tp.ticketType);
                      return (
                        <button
                          type="button"
                          key={tp.ticketType}
                          onClick={() => setFormData((prev) => ({ ...prev, allowedTicketTypes: toggleInArray(prev.allowedTicketTypes || [], tp.ticketType) }))}
                          className={`px-3 py-1.5 rounded-full text-sm border ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-300"}`}
                        >
                          {tp.ticketType}
                        </button>
                      );
                    })}
                  </div>
                  <TwFormHelperText>
                    {(formData.allowedTicketTypes || []).length === 0
                      ? t("myPromotions.allTicketsIncluded", "All ticket types are included.")
                      : t("myPromotions.onlySelectedTickets", "Only selected ticket types show on this link.")}
                  </TwFormHelperText>
                </div>
              )}

              {/* Commission model type */}
              <fieldset aria-label="Commission model type">
                <TwLabel>{t("myEvents.commissionModelType")}</TwLabel>
                <RadioGroup
                  value={formData.commissionModelType}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, commissionModelType: value }));
                    let setTiersData = [];
                    if (value === "flat") {
                      setTiersData = [{ from: 1, commissionPerTicket: 1 }];
                    } else if (value === "thresholdBased") {
                      setTiersData = [
                        { from: 1, to: 9, commissionPerTicket: 0 },
                        { from: 10, to: "", commissionPerTicket: 0 },
                      ];
                    } else {
                      setTiersData = [
                        { from: 1, to: 10, commissionPerTicket: 0 },
                        { from: 11, to: "", commissionPerTicket: 0 },
                      ];
                    }
                    setTiers([...setTiersData]);
                  }}
                  className="grid grid-cols-1 gap-x-1 rounded-full bg-white/5 p-1 text-center text-xs/5 font-semibold text-gray-900"
                >
                  <div className="space-x-4 flex">
                    {["flat", "tiered", "thresholdBased"].map((option) => (
                      <Radio key={option} value={option} className="flex items-center space-x-3 cursor-pointer">
                        {({ checked }) => (
                          <>
                            <span className={`h-5 w-5 shrink-0 aspect-square rounded-full border-2 flex items-center justify-center ${checked ? "border-blue-600" : "border-gray-300"}`}>
                              {checked && <span className="h-2.5 w-2.5 shrink-0 bg-blue-600 rounded-full" />}
                            </span>
                            <span className="text-gray-900 font-medium capitalize">{t("myEvents." + option)}</span>
                          </>
                        )}
                      </Radio>
                    ))}
                  </div>
                </RadioGroup>
              </fieldset>
              {formData.commissionModelType === "flat" && (
                <TwFormHelperText>{t("myEvents.commissionModelTypeFlatHelper")}</TwFormHelperText>
              )}
              {formData.commissionModelType === "tiered" && (
                <TwFormHelperText>{t("myEvents.commissionModelTypeTieredHelper")}</TwFormHelperText>
              )}
              {formData.commissionModelType === "thresholdBased" && (
                <TwFormHelperText>{t("myEvents.commissionModelTypeThresholdHelper")}</TwFormHelperText>
              )}

              {/* Unit type */}
              <TwSelectGroup
                label={t("myEvents.commissionUnitType")}
                placeholder={t("myEvents.commissionUnitType")}
                name="unitType"
                selectClass="!py-3 md:!py-3"
                options={Object.keys(commissionType).map((d) => ({
                  value: d,
                  title: commissionType[d],
                }))}
                defaultValue={formData.unitType}
                value={formData.unitType}
                onChange={(event) => {
                  setFormData((prev) => ({ ...prev, unitType: event.target.value }));
                }}
                errors={formErrors}
              />

              {/* Flat value or Tiered/Threshold */}
              {formData.commissionModelType === "flat" ? (<>
                <TwInputGroup
                  label={`${t("myEvents.entervalue")} ${formData.unitType === "percentageOnTicketPrice" ? "%" : "€"}`}
                  placeholder={`${t("myEvents.entervalue")} ${formData.unitType === "percentageOnTicketPrice" ? "%" : "€"}`}
                  type="text"
                  inputMode="decimal"
                  name="flatValue"
                  defaultValue={tiers.length > 0 ? tiers[0].commissionPerTicket : 0}
                  value={decimalDisplayValue(formData.flatValue || 0, i18n.language)}
                  onChange={(event) => {
                    const parsed = decimalOnChange(event.target.value, i18n.language);
                    if (parsed !== null) {
                      setTiers([{ from: 1, commissionPerTicket: parsed }]);
                      setFormData((prev) => ({ ...prev, flatValue: parsed }));
                    }
                  }}
                  errors={formErrors}
                  className="!py-3 md:pl-2.5"
                />
              </>) : (<div className="flex flex-col gap-3">
                <TwLabel>{formData.commissionModelType === "thresholdBased" ? t("myEvents.thresholds") : t("myEvents.tiers")}</TwLabel>
                {tiers.map((tier, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    {formData.commissionModelType === "thresholdBased" && (<div className="w-full flex flex-col gap-0.5">
                      <TwLabel className="text-xs mt-6">{t("myEvents.greaterThan")}&nbsp;<br />/&nbsp;{t("myEvents.equalTo")}</TwLabel>
                    </div>)}
                    <div className="w-full flex flex-col gap-0.5">
                      <TwLabel className="text-xs">{formData.commissionModelType === "thresholdBased" ? t("myEvents.ticketsSold") : t("myEvents.from")}</TwLabel>
                      <input
                        type="number"
                        title={t("myEvents.min")}
                        placeholder={t("myEvents.min")}
                        disabled={formData.commissionModelType === "thresholdBased" && index === 0}
                        className="mt-1 w-full rounded-2xl border border-gray-300 p-2 focus:ring-black focus:border-black"
                        value={tier.from}
                        onChange={(e) => handleTierChange(index, "from", e.target.value)}
                      />
                    </div>
                    <div className={formData.commissionModelType === "thresholdBased" ? "hidden" : "w-full flex flex-col gap-0.5"}>
                      <TwLabel className="text-xs">{t("myEvents.to")}</TwLabel>
                      <input
                        type="number"
                        placeholder={t("myEvents.max")}
                        className="mt-1 w-full rounded-2xl border border-gray-300 p-2 focus:ring-black focus:border-black"
                        disabled={formData.commissionModelType === "thresholdBased" || tiers.length === (index + 1)}
                        value={tier.to}
                        onChange={(e) => handleTierChange(index, "to", e.target.value)}
                      />
                    </div>
                    <div className="w-full flex flex-col gap-0.5">
                      <TwLabel className="text-xs">{`${t("myEvents.rate")} ${formData.unitType === "percentageOnTicketPrice" ? "%" : "€"}`}</TwLabel>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={`${t("myEvents.rate")} ${formData.unitType === "percentageOnTicketPrice" ? "%" : "€"}`}
                        className="mt-1 w-full rounded-2xl border border-gray-300 p-2 focus:ring-black focus:border-black"
                        value={decimalDisplayValue(tier.commissionPerTicket, i18n.language)}
                        onChange={(e) => {
                          const parsed = decimalOnChange(e.target.value, i18n.language);
                          if (parsed !== null) handleTierChange(index, "commissionPerTicket", parsed);
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <TwLabel className="text-xs">&nbsp;</TwLabel>
                      <button
                        onClick={() => removeTier(index)}
                        className={tiers.length > 2 ? "" : "hidden"}>
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                {formErrors.tiers && (<TwFormHelperText error={formErrors.tiers}>{formErrors.tiers}</TwFormHelperText>)}
                <button
                  type="button"
                  onClick={addTier}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {formData.commissionModelType === "thresholdBased" ? t("myEvents.addThreshold") : t("myEvents.addTier")}
                </button>
              </div>)}

              <div className="border-b border-gray-900/10 pb-1"></div>

              {/* Bonus */}
              <TwSwitch
                title={t("myEvents.addBonus")}
                name="isAddBonus"
                checked={formData.isAddBonus}
                onChange={() => {
                  setFormData((prev) => ({
                    ...prev,
                    isAddBonus: !formData.isAddBonus,
                    ...((!formData.isAddBonus) === false) ? { bonusThreshold: 0, bonusAmount: 0 } : {},
                  }));
                }}
              />
              {formData.isAddBonus && (<>
                <TwInputGroup
                  label={t("myEvents.bonusThreshold")}
                  placeholder={t("myEvents.bonusThreshold")}
                  type="number"
                  name="bonusThreshold"
                  value={formData.bonusThreshold}
                  onChange={(event) => {
                    setFormData((prev) => ({ ...prev, bonusThreshold: event.target.value }));
                  }}
                  errors={formErrors}
                  className="!py-3 md:pl-2.5"
                />
                <TwInputGroup
                  label={t("myEvents.bonusAmount")}
                  placeholder={t("myEvents.bonusAmount")}
                  type="text"
                  inputMode="decimal"
                  name="bonusAmount"
                  value={decimalDisplayValue(formData.bonusAmount, i18n.language)}
                  onChange={(event) => {
                    const parsed = decimalOnChange(event.target.value, i18n.language);
                    if (parsed !== null) {
                      setFormData((prev) => ({ ...prev, bonusAmount: parsed }));
                    }
                  }}
                  errors={formErrors}
                  className="!py-3 md:pl-2.5"
                />
              </>)}

              {/* Add-ons */}
              {scheduleAddons.length > 0 && (
                <>
                  <div className="border-b border-gray-900/10 pb-1"></div>
                  <TwSwitch
                    title={t("myPromotions.includeAddons", "Include add-ons")}
                    name="applyToAddons"
                    checked={formData.applyToAddons}
                    onChange={() => setFormData((prev) => ({ ...prev, applyToAddons: !prev.applyToAddons }))}
                  />
                  {formData.applyToAddons && (
                    <>
                      <div>
                        <TwLabel>{t("myPromotions.whichAddons", "Which add-ons")}</TwLabel>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {scheduleAddons.map((ad) => {
                            const active = (formData.allowedAddonIds || []).includes(ad.id);
                            return (
                              <button
                                type="button"
                                key={ad.id}
                                onClick={() => setFormData((prev) => ({ ...prev, allowedAddonIds: toggleInArray(prev.allowedAddonIds || [], ad.id) }))}
                                className={`px-3 py-1.5 rounded-full text-sm border ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-300"}`}
                              >
                                {ad.addonName}
                              </button>
                            );
                          })}
                        </div>
                        {(formData.allowedAddonIds || []).length === 0 && (
                          <TwFormHelperText>{t("myPromotions.selectAddonsToInclude", "Select the add-ons to include. If none are selected, no add-ons will be shown on the promoter's link.")}</TwFormHelperText>
                        )}
                      </div>
                      <TwSwitch
                        title={t("myPromotions.addonSameAsTickets", "Same commission as tickets")}
                        name="addonSameAsTickets"
                        checked={formData.addonSameAsTickets}
                        onChange={() => setFormData((prev) => ({ ...prev, addonSameAsTickets: !prev.addonSameAsTickets }))}
                      />
                      {!formData.addonSameAsTickets && (
                        <CommissionModelBlock
                          value={addonForm}
                          onChange={(patch) => setAddonForm((prev) => ({ ...prev, ...patch }))}
                          tiers={addonTiers}
                          setTiers={setAddonTiers}
                          errors={formErrors}
                        />
                      )}
                    </>
                  )}
                </>
              )}
              </>)}
            </div>
          </TwDrawer>

          {/* ─── Add Sellers Drawer ─── */}
          <TwDrawer
            title={t("myEvents.addSellers")}
            classNameDialogPanel="!max-w-md"
            open={inviteDrawerOpen}
            onClose={() => {
              setInviteDrawerOpen(false);
              setInviteResult(null);
            }}
            footerChildren={
              inviteResult?.success ? (
                <TwButton
                  variant="secondary"
                  onClick={() => {
                    setInviteDrawerOpen(false);
                    setInviteResult(null);
                  }}
                >
                  {t("inputFieldsTitle.close") || "Close"}
                </TwButton>
              ) : (
                <>
                  <TwButton
                    variant="secondary-outline"
                    onClick={() => {
                      setInviteDrawerOpen(false);
                      setInviteResult(null);
                    }}
                  >
                    {t("inputFieldsTitle.cancel")}
                  </TwButton>
                  <TwButton
                    variant="secondary"
                    disabled={selectedSellerIds.length === 0 || sendingInvites}
                    actionLoading={sendingInvites}
                    onClick={handleSendInvites}
                  >
                    <EnvelopeIcon className="size-4 mr-1" />
                    {addMode === "managed" ? t("myEvents.addSellers") : t("myEvents.sendInvites")}
                  </TwButton>
                </>
              )
            }
          >
            <div className="flex flex-col gap-4">
              {inviteResult?.success ? (
                <div className="text-center py-6">
                  <div className="text-green-600 text-lg font-semibold mb-2">
                    {inviteResult.mode === "managed" ? t("myEvents.sellersAdded") : t("myPromotions.invitesSent")}
                  </div>
                  <p className="text-sm text-gray-500">
                    {inviteResult.data.filter((r) => r.status === "sent" || r.emailStatus === "sent").length} {t("myPromotions.invitesSentCount")}
                  </p>
                </div>
              ) : (
                <>
                  {/* Mode: invite (self-serve) vs managed (add now) */}
                  <div className="flex flex-col gap-2">
                    {[
                      { value: "invite", title: t("myEvents.modeInvite"), desc: t("myEvents.modeInviteDesc") },
                      { value: "managed", title: t("myEvents.modeManaged"), desc: t("myEvents.modeManagedDesc") },
                    ].map((opt) => {
                      const active = addMode === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setAddMode(opt.value)}
                          className={`flex items-start gap-3 text-left rounded-xl border p-3 transition-colors ${active ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}
                        >
                          <span className={`mt-0.5 h-5 w-5 shrink-0 aspect-square rounded-full border-2 flex items-center justify-center ${active ? "border-blue-600" : "border-gray-300"}`}>
                            {active && <span className="h-2.5 w-2.5 shrink-0 bg-blue-600 rounded-full" />}
                          </span>
                          <span className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{opt.title}</span>
                            <span className="text-xs text-gray-500">{opt.desc}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <p className="text-sm text-gray-600 flex items-center justify-between">
                    <span>{t("myPromotions.selectSellersToInvite")}</span>
                    {sellersList.length > 0 && (
                      <span className="text-xs font-medium text-gray-400 shrink-0">
                        {sellersList.length} {sellersList.length === 1 ? (t("myPromotions.sellerSingular") || "seller") : (t("myPromotions.sellers") || "sellers")}
                      </span>
                    )}
                  </p>
                  {sellersList.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">
                      {t("myPromotions.noSellersFound")}
                    </p>
                  ) : (
                    <div className="relative">
                    <div
                      ref={sellerListRef}
                      onScroll={updateSellerScrollHint}
                      className="max-h-[60vh] overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100"
                    >
                      {sellersList.map((seller) => {
                        // Already on this campaign → ticked + locked (no re-invite).
                        const enrolled = enrolledSellerIds.includes(String(seller._id));
                        // No email → can't send an invite link → locked with a hint.
                        const noEmail = !seller.email || seller.email === "";
                        const locked = enrolled || noEmail;
                        const isChecked = enrolled || selectedSellerIds.includes(seller._id);
                        return (
                          <label
                            key={seller._id}
                            className={`flex items-center gap-3 px-3 py-3 transition-colors ${locked ? "cursor-not-allowed bg-gray-50 opacity-70" : `cursor-pointer ${isChecked ? "bg-blue-50" : "hover:bg-gray-50"}`}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={locked}
                              onChange={() => { if (!locked) toggleSellerSelection(seller._id); }}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {seller.name || seller.displayName || "Seller"}
                                {enrolled && (
                                  <span className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                    {t("myPromotions.alreadyAdded") || "Already added"}
                                  </span>
                                )}
                                {!enrolled && noEmail && (
                                  <span className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                                    {t("myPromotions.noEmail") || "No email"}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {seller.email || (t("myPromotions.noEmailHint") || "Add an email to this seller to invite them")}
                              </p>
                              {seller.channeltype && (
                                <p className="text-xs text-gray-400 capitalize">{seller.channeltype.replace(/_/g, " ")}</p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {/* Overflow cue: fade + bouncing chevron while more is below. */}
                    {canScrollSellers && (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 rounded-b-xl bg-gradient-to-t from-white via-white/80 to-transparent flex items-end justify-center pb-1.5">
                        <button
                          type="button"
                          onClick={() => sellerListRef.current && sellerListRef.current.scrollBy({ top: 240, behavior: "smooth" })}
                          className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 shadow ring-1 ring-gray-200 animate-bounce"
                        >
                          <ChevronDownIcon className="size-3.5" />
                          {t("myPromotions.scrollForMore") || "Scroll for more"}
                        </button>
                      </div>
                    )}
                    </div>
                  )}
                  {selectedSellerIds.length > 0 && (
                    <p className="text-xs text-gray-500">
                      {selectedSellerIds.length} {t("myPromotions.sellersSelected")}
                    </p>
                  )}
                </>
              )}
            </div>
          </TwDrawer>
        </TwContainer>
      }
    />
  );
}
